import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Adventure } from '@gm-binder/shared';
import { Modal } from '../ui';
import styles from './SearchPalette.module.css';

export interface SearchPaletteProps {
  open: boolean;
  onClose: () => void;
  adventure: Adventure;
  instanceId: string;
}

interface Hit {
  type: string;
  name: string;
  to: string;
}

/** Global Ctrl/Cmd-K search over rooms, characters, NPCs, items and clues. */
export function SearchPalette({ open, onClose, adventure, instanceId }: SearchPaletteProps) {
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const base = `/run/${instanceId}`;

  const hits = useMemo<Hit[]>(() => {
    const term = q.trim().toLowerCase();
    if (term.length < 2) return [];
    const out: Hit[] = [];
    const match = (...vals: (string | undefined)[]) =>
      vals.some((v) => v?.toLowerCase().includes(term));

    adventure.rooms.rooms.forEach((r) => {
      if (match(r.name, r.description)) out.push({ type: 'Stanza', name: r.name, to: `${base}/rooms/${r.id}` });
    });
    adventure.characters.forEach((c) => {
      if (match(c.name, c.class, c.race))
        out.push({ type: 'Personaggio', name: c.name, to: `${base}/characters/${encodeURIComponent(c.name)}` });
    });
    adventure.npcs.forEach((n) => {
      if (match(n.name, n.role, n.description))
        out.push({ type: 'PNG', name: n.name, to: `${base}/npcs${n.id ? `?focus=${n.id}` : ''}` });
    });
    adventure.items.forEach((i) => {
      if (match(i.name, i.description))
        out.push({ type: 'Oggetto', name: i.name, to: `${base}/items${i.id ? `?focus=${i.id}` : ''}` });
    });
    adventure.clues.forEach((c) => {
      if (match(c.title, c.text))
        out.push({ type: 'Indizio', name: c.title, to: `${base}/clues${c.id ? `?focus=${c.id}` : ''}` });
    });
    return out.slice(0, 30);
  }, [q, adventure, base]);

  const go = (to: string) => {
    onClose();
    setQ('');
    navigate(to);
  };

  return (
    <Modal open={open} onClose={onClose} title="🔎 Cerca" width={620}>
      <input
        className={styles.input}
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Cerca stanze, personaggi, PNG, oggetti, indizi…"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && hits[0]) go(hits[0].to);
        }}
      />
      <div className={styles.results}>
        {q.trim().length < 2 ? (
          <div className={styles.muted}>Digita almeno 2 caratteri…</div>
        ) : hits.length === 0 ? (
          <div className={styles.muted}>Nessun risultato</div>
        ) : (
          hits.map((h, i) => (
            <button key={`${h.type}-${h.name}-${i}`} className={styles.result} onClick={() => go(h.to)}>
              <span className={styles.type}>{h.type}</span>
              <span className={styles.name}>{h.name}</span>
            </button>
          ))
        )}
      </div>
    </Modal>
  );
}
